import { Reducer } from "react";

export type WithHistory<S> = {
  past: S[];
  current: S;
  future: S[];
};

/**
 * A "Reducer enhancer" that adds undo/redo capabilities to the specified reducer
 * This function is a slightly modified version of what's covered in https://redux.js.org/usage/implementing-undo-history
 *
 * @param reducer
 * @returns A new reducer that includes "past", "current" and "future" properties
 */
export function undoable<S, A extends { type: string }>(
  reducer: Reducer<S, A>
) {
  // Return a reducer that handles undo and redo
  return function (state: WithHistory<S>, action: A) {
    const { past, current, future } = state;
    switch (action.type) {
      case "undo":
        const previous = past[past.length - 1];
        const newPast = past.slice(0, past.length - 1);
        return {
          past: newPast,
          current: previous,
          future: [current, ...future],
        };
      case "redo":
        const next = future[0];
        const newFuture = future.slice(1);
        return {
          past: [...past, current],
          current: next,
          future: newFuture,
        };
      default:
        // Delegate handling the action to the passed reducer
        const newCurrent = reducer(current, action);
        if (current === newCurrent) {
          return state;
        }
        return {
          past: [...past, current],
          current: newCurrent,
          future: [],
        };
    }
  };
}
